<template>
  <v-dialog v-model="dialog" width="1000" max-width="95vw" persistent>
    <v-card>
      <v-card-title>
        <h3><strong>{{ $t('importResultDialog.label.title', { name: title }) }}</strong></h3>
      </v-card-title>
      <v-card-text>
        <table class="info">
          <tbody>
            <tr>
              <th>{{ $t('importResultDialog.label.fileName') }}</th>
              <td>{{ fileName }}</td>
              <th>{{ $t('importResultDialog.label.processedAt') }}</th>
              <td>{{ processedAt }}</td>
            </tr>
            <tr>
              <th>{{ $t('importResultDialog.label.state') }}</th>
              <td colspan="3" :class="result.registered ? 'state_ok' : 'state_ng'">
                {{ result.registered ? $t('importResultDialog.label.registered') : $t('importResultDialog.label.notRegistered') }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 件数。誤りがあると1件も登録しないので、そのときは追加・上書きの件数は出さない -->
        <div class="tiles">
          <div class="tile">
            <div class="tile_label">{{ $t('importResultDialog.label.readCount') }}</div>
            <div class="tile_value">{{ result.readCount }}</div>
          </div>
          <div class="tile">
            <div class="tile_label">{{ $t('importResultDialog.label.okCount') }}</div>
            <div class="tile_value ok">{{ result.okCount }}</div>
          </div>
          <div class="tile">
            <div class="tile_label">{{ $t('importResultDialog.label.errorCount') }}</div>
            <div class="tile_value ng">{{ result.errorRowCount }}</div>
          </div>
          <template v-if="result.registered">
            <div class="tile" v-if="result.addedCount != null">
              <div class="tile_label">{{ $t('importResultDialog.label.addedCount') }}</div>
              <div class="tile_value">{{ result.addedCount }}</div>
            </div>
            <div class="tile" v-if="result.updatedCount != null">
              <div class="tile_label">{{ $t('importResultDialog.label.updatedCount') }}</div>
              <div class="tile_value">{{ result.updatedCount }}</div>
            </div>
            <div class="tile" v-if="result.deletedCount != null">
              <div class="tile_label">{{ $t('importResultDialog.label.deletedCount') }}</div>
              <div class="tile_value">{{ result.deletedCount }}</div>
            </div>
          </template>
        </div>

        <div v-if="errorMessage" class="message_ng mt-3">{{ errorMessage }}</div>
        <div v-if="rowErrors.length > 0" class="mt-1">{{ $t('importResultDialog.message.fixAndRetry') }}</div>

        <template v-if="rowErrors.length > 0">
          <div class="text-subtitle-2 mt-3 mb-1">{{ $t('importResultDialog.label.errorRows') }}</div>
          <div class="rows_box">
            <table class="rows">
              <thead>
                <tr>
                  <th class="nowrap">{{ $t('importResultDialog.label.rowNo') }}</th>
                  <th v-for="c in columns" :key="c" class="nowrap">{{ c }}</th>
                  <th>{{ $t('importResultDialog.label.reason') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in rowErrors" :key="r.rowNo">
                  <td class="nowrap">{{ r.rowNo }}</td>
                  <td v-for="(v, i) in columns" :key="i" class="nowrap">{{ (r.values ?? [])[i] }}</td>
                  <td class="reason">{{ r.message }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-btn v-if="rowErrors.length > 0" dark class="bg-indigo-darken-1" elevation="2" @click="downloadErrorCsv">
          {{ $t('importResultDialog.action.downloadErrors') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="handleClose">{{ $t('importResultDialog.action.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * ファイル取込（ユーザーCSV取込・企業コード一括登録）の結果。
 *
 * 何行読んで、何行が正しく、何行に誤りがあったかを見せ、誤りのあった行は行番号・中身・理由を表にする。
 * 誤りの行はCSVで出せる（「エラーCSV出力」。JCIKAIHATSU-496 受入テスト 2026-11-03）。
 * 中身はサーバーの importResult（ImportResultForm）をそのまま使う。パスワードはサーバーが返さない。
 */
export default {
  props: {
    // 何の取込か（「ユーザーCSV取込」など）。タイトルとCSVのファイル名に使う
    title: {
      type: String,
      required: true,
    },
    // 取り込んだファイルの名前
    fileName: {
      type: String,
      default: "",
    },
    // サーバーの importResult
    result: {
      type: Object,
      required: true,
    },
    // サーバーの errorMessage（「入力エラーがあります」など）
    errorMessage: {
      type: String,
      default: "",
    },
    handleClose: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      dialog: true,
      // 結果を受け取った時刻（画面を開いた時刻）
      processedAt: formatDateTime(new Date()),
    };
  },

  computed: {
    columns() {
      return this.result.columns ?? [];
    },
    rowErrors() {
      return this.result.rowErrors ?? [];
    },
  },

  methods: {
    // 誤りのあった行をCSVにして保存させる。Excelで開いても文字化けしないよう UTF-8 の BOM を付ける
    downloadErrorCsv() {
      const header = [this.$t('importResultDialog.label.rowNo'), ...this.columns, this.$t('importResultDialog.label.reason')];
      const lines = [header, ...this.rowErrors.map((r) => [String(r.rowNo), ...this.columns.map((_, i) => (r.values ?? [])[i] ?? ""), r.message ?? ""])]
        .map((cols) => cols.map(csvCell).join(","));
      const blob = new Blob(["﻿" + lines.join("\r\n") + "\r\n"], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = errorCsvFileName(this.fileName, this.title);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};

// カンマ・ダブルクォート・改行を含む値は "" で囲み、中の " は "" にする
function csvCell(value) {
  const s = String(value ?? "");
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// 元のファイル名に「_エラー」を付ける（拡張子は .csv）
function errorCsvFileName(fileName, title) {
  const base = (fileName || title).replace(/\.[^.]+$/, "");
  return base + "_エラー.csv";
}

function formatDateTime(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}
</script>

<style scoped>
.info {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  margin-bottom: 12px;
}
.info th,
.info td {
  padding: 6px 8px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
}
.info th {
  width: 110px;
  font-weight: normal;
  color: #555;
  white-space: nowrap;
}
.state_ok {
  color: #2e7d32;
  font-weight: bold;
}
.state_ng {
  color: #c62828;
  font-weight: bold;
}
.tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.tile {
  flex: 1 1 110px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  text-align: center;
}
.tile_label {
  font-size: 12px;
  color: #555;
}
.tile_value {
  font-size: 22px;
  font-weight: bold;
}
.tile_value.ok {
  color: #2e7d32;
}
.tile_value.ng {
  color: #c62828;
}
.message_ng {
  color: #c62828;
}
.rows_box {
  max-height: 320px;
  overflow: auto;
  border: 1px solid #1565c0;
  border-radius: 4px;
}
.rows {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.rows th,
.rows td {
  padding: 4px 8px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  vertical-align: top;
}
.rows th {
  position: sticky;
  top: 0;
  background: #f5f5f5;
}
/* 行の中身は折り返さない（横に長い分は枠の中で横にスクロールする）。理由だけは長いので折り返す */
.rows .reason {
  color: #c62828;
  min-width: 240px;
}
.nowrap {
  white-space: nowrap;
}
</style>
